import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Plus, Building2, Trash2, ArrowRightLeft, Loader2,
} from 'lucide-react';

const COLUMNS = [
  { id: 'NEW', label: 'Nouveau', color: 'bg-slate-500', accent: 'border-slate-500/40' },
  { id: 'QUALIFIED', label: 'Qualifié', color: 'bg-blue-500', accent: 'border-blue-500/40' },
  { id: 'PROPOSAL_SENT', label: 'Devis envoyé', color: 'bg-amber-500', accent: 'border-amber-500/40' },
  { id: 'NEGOTIATION', label: 'Négociation', color: 'bg-purple-500', accent: 'border-purple-500/40' },
  { id: 'WON', label: 'Gagné', color: 'bg-emerald-500', accent: 'border-emerald-500/40' },
  { id: 'LOST', label: 'Perdu', color: 'bg-red-500', accent: 'border-red-500/40' },
];

const SOURCES = [
  { value: 'website', label: 'Site web' },
  { value: 'referral', label: 'Recommandation' },
  { value: 'social', label: 'Réseaux sociaux' },
  { value: 'event', label: 'Salon / Événement' },
  { value: 'cold_call', label: 'Démarchage' },
  { value: 'other', label: 'Autre' },
];

const LeadCard = ({ lead, index, onEdit, onDelete }) => (
  <Draggable draggableId={lead.id} index={index}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        data-testid={`lead-card-${lead.id}`}
        className={`mb-2 transition-shadow ${snapshot.isDragging ? 'z-50' : ''}`}
        style={{
          ...provided.draggableProps.style,
          transform: snapshot.isDragging
            ? `${provided.draggableProps.style?.transform || ''} rotate(2deg)`
            : provided.draggableProps.style?.transform,
        }}
      >
        <Card className={`cursor-grab active:cursor-grabbing select-none ${snapshot.isDragging ? 'shadow-xl shadow-primary/20 ring-2 ring-primary/30' : 'hover:shadow-md'}`}>
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0" onClick={() => onEdit(lead)}>
                <p className="font-medium text-sm truncate">{lead.name}</p>
                {lead.company && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Building2 className="w-3 h-3" /> {lead.company}
                  </p>
                )}
                {lead.estimated_value > 0 && (
                  <p className="text-xs font-medium text-primary mt-1">
                    {formatCurrency(lead.estimated_value)}
                  </p>
                )}
                {lead.source && (
                  <Badge variant="outline" className="text-[10px] mt-1">
                    {SOURCES.find(s => s.value === lead.source)?.label || lead.source}
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost" size="icon"
                className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onDelete(lead.id); }}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )}
  </Draggable>
);

const KanbanColumn = ({ column, leads, onEdit, onDelete }) => {
  const total = leads.reduce((s, lead) => s + (lead.estimated_value || 0), 0);
  return (
    <div className="flex-1 min-w-[210px] max-w-[280px]" data-testid={`kanban-column-${column.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-2.5 h-2.5 rounded-full ${column.color}`} />
        <h3 className="text-sm font-medium">{column.label}</h3>
        <Badge variant="secondary" className="text-xs ml-auto">{leads.length}</Badge>
      </div>
      {total > 0 && (
        <p className="text-xs text-muted-foreground mb-2">{formatCurrency(total)}</p>
      )}
      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`min-h-[120px] p-1.5 rounded-lg transition-colors duration-150 border-2 border-dashed ${
              snapshot.isDraggingOver
                ? `bg-primary/5 ${column.accent}`
                : 'bg-secondary/20 border-transparent'
            }`}
          >
            {leads.map((lead, index) => (
              <LeadCard key={lead.id} lead={lead} index={index} onEdit={onEdit} onDelete={onDelete} />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

const LeadFormDialog = ({ lead, open, onOpenChange, onSave }) => {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', source: 'none', estimated_value: '', notes: '', status: 'NEW' });

  React.useEffect(() => {
    if (lead) {
      setForm({ ...lead, source: lead.source || 'none', estimated_value: lead.estimated_value?.toString() || '' });
    } else {
      setForm({ name: '', company: '', email: '', phone: '', source: 'none', estimated_value: '', notes: '', status: 'NEW' });
    }
  }, [lead, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: form.name,
      company: form.company || null,
      email: form.email || null,
      phone: form.phone || null,
      source: form.source === 'none' ? null : form.source,
      estimated_value: parseFloat(form.estimated_value) || 0,
      notes: form.notes || null,
      status: form.status || 'NEW',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{lead ? 'Modifier le lead' : 'Nouveau lead'}</DialogTitle>
          <DialogDescription>Informations du prospect</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom / Contact *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="lead-name" />
            </div>
            <div className="space-y-2">
              <Label>Entreprise</Label>
              <Input value={form.company || ''} onChange={e => setForm({ ...form, company: e.target.value })} data-testid="lead-company" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="lead-email" />
            </div>
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Source</Label>
              <Select value={form.source || 'none'} onValueChange={v => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non définie</SelectItem>
                  {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Valeur estimée (€)</Label>
              <Input type="number" step="0.01" value={form.estimated_value} onChange={e => setForm({ ...form, estimated_value: e.target.value })} data-testid="lead-value" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" data-testid="lead-submit">{lead ? 'Enregistrer' : 'Créer'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ConvertDialog = ({ lead, open, onOpenChange, onConfirm, loading }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5" /> Convertir en client
        </DialogTitle>
        <DialogDescription>
          Le lead <strong>{lead?.name}</strong> sera créé comme client dans votre base.
        </DialogDescription>
      </DialogHeader>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        <Button onClick={onConfirm} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-convert-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Créer le client
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

const LeadsPage = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [convertLead, setConvertLead] = useState(null);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['leads'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/leads`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); toast.success('Lead créé'); setFormOpen(false); },
    onError: () => toast.error('Erreur'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/leads/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); },
    onError: () => toast.error('Erreur'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/leads/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); toast.success('Lead supprimé'); },
  });

  const convertMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/leads/${id}/convert`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success(`Client "${data.client?.name}" créé`);
      setConvertLead(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleSave = (data) => {
    if (editingLead) {
      updateMutation.mutate({ id: editingLead.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDragEnd = (result) => {
    const { draggableId, destination, source } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const targetStatus = destination.droppableId;
    const lead = leads.find(ld => ld.id === draggableId);
    if (!lead) return;

    // If moving to WON and not already converted, show convert dialog
    if (targetStatus === 'WON' && !lead.converted_client_id) {
      updateMutation.mutate({ id: draggableId, data: { status: 'WON' } });
      setTimeout(() => setConvertLead({ ...lead, status: 'WON' }), 300);
      return;
    }

    updateMutation.mutate({ id: draggableId, data: { status: targetStatus } });
  };

  const columnLeads = useMemo(() => {
    const map = {};
    COLUMNS.forEach(col => { map[col.id] = leads.filter(ld => ld.status === col.id); });
    return map;
  }, [leads]);

  const totalValue = leads.reduce((s, ld) => s + (ld.estimated_value || 0), 0);
  const wonValue = leads.filter(ld => ld.status === 'WON').reduce((s, ld) => s + (ld.estimated_value || 0), 0);
  const convRate = leads.length > 0 ? Math.round(leads.filter(ld => ld.status === 'WON').length / leads.length * 100) : 0;

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Prospection</h1>
          <p className="text-muted-foreground">Pipeline commercial - Suivi des leads</p>
        </div>
        <Button onClick={() => { setEditingLead(null); setFormOpen(true); }} data-testid="add-lead-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau lead
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">Total leads</p>
          <p className="text-xl font-heading font-bold">{leads.length}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">Pipeline</p>
          <p className="text-xl font-heading font-bold">{formatCurrency(totalValue)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">Gagné</p>
          <p className="text-xl font-heading font-bold text-emerald-500">{formatCurrency(wonValue)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">Taux conversion</p>
          <p className="text-xl font-heading font-bold">{convRate}%</p>
        </CardContent></Card>
      </div>

      {isLoading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map(col => (
            <div key={col.id} className="flex-1 min-w-[210px]">
              <Skeleton className="h-6 w-24 mb-3" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          ))}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {COLUMNS.map(col => (
              <KanbanColumn
                key={col.id}
                column={col}
                leads={columnLeads[col.id] || []}
                onEdit={(ld) => { setEditingLead(ld); setFormOpen(true); }}
                onDelete={(id) => { if (window.confirm('Supprimer ce lead ?')) deleteMutation.mutate(id); }}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      <LeadFormDialog
        lead={editingLead}
        open={formOpen}
        onOpenChange={(o) => { setFormOpen(o); if (!o) setEditingLead(null); }}
        onSave={handleSave}
      />

      <ConvertDialog
        lead={convertLead}
        open={!!convertLead}
        onOpenChange={(o) => { if (!o) setConvertLead(null); }}
        onConfirm={() => convertMutation.mutate(convertLead.id)}
        loading={convertMutation.isPending}
      />
    </div>
  );
};

export default LeadsPage;
